import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  listColourPatchSchema,
  listPrListPatchSchema,
  listSchema,
  listMovePreviewQuerySchema,
  listMoveToBoardBodySchema,
  listMoveToBoardResultSchema,
  listListSchema,
  reconciliationSchema,
  type BoardContents,
  type BoardFaceList,
  type CreateListBody,
  type List,
  type ListColour,
  type ListColourPatch,
  type ListMovePreviewQuery,
  type ListMoveToBoardBody,
  type ListMoveToBoardResult,
  type ListPositionPatch,
  type ListPrListPatch,
  type ListTitlePatch,
  type Reconciliation,
} from '@molt/shared';
import { announce } from './announce';
import { del, emptyResponseSchema, get, patch, post } from './api';
import {
  boardMoveInvalidationKeys,
  removeListFromBoardContents,
  setBoardContentsQueries,
} from './cross-board-move';
import { optimisticMutation } from './optimistic';
import { useInterfaceLanguage } from './interface-language';
import { queryKeys } from './query-keys';

/**
 * The list mutations — chunk `C2`, agent A (`FE-11.1`, `FE-11.2`, `FE-11.3`).
 *
 * THE RULE THIS FILE HOLDS (brief §3.1): the client never computes a rank.
 * `PATCH /lists/:id/position` takes the two lists FLANKING the gap the dragged
 * list was dropped into, and there is no `position` key on any request body in
 * this product. Nothing here imports `rankBetween`, `ranksBetween`,
 * `firstRank` or `compareRanks` from `@molt/shared` — they are exported, and
 * they are not this file's.
 *
 * The corollary, brief §5.2: an optimistic update cannot produce a valid
 * `position`, so a move is a SPLICE OF THE ARRAY and every list's `position`
 * field is left exactly as it was. Nothing in `apps/web` sorts by `position`
 * — `BoardFace` renders `contents.lists` in payload order — so the array is
 * the render order and the stale rank is unused until the refetch lands.
 *
 * This file spells its own paths. `lib/api.ts`'s own comment forbids adding
 * route strings to its `api` object: a block's endpoint function is one line
 * on top of one of its verbs, in the block's own file.
 */

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export function createList(body: CreateListBody): Promise<List> {
  return post('/lists', listSchema, body);
}

export function renameList(listId: string, body: ListTitlePatch): Promise<List> {
  return patch(`/lists/${encodeURIComponent(listId)}/title`, listSchema, body);
}

export function recolourList(listId: string, body: ListColourPatch): Promise<List> {
  return patch(
    `/lists/${encodeURIComponent(listId)}/colour`,
    listSchema,
    listColourPatchSchema.parse(body),
  );
}

/** `PATCH /lists/:id/pr-list` — designate (or clear) the board's PR list. */
export function setListPrList(listId: string, body: ListPrListPatch): Promise<List> {
  return patch(
    `/lists/${encodeURIComponent(listId)}/pr-list`,
    listSchema,
    listPrListPatchSchema.parse(body),
  );
}

export function moveList(listId: string, body: ListPositionPatch): Promise<List> {
  return patch(`/lists/${encodeURIComponent(listId)}/position`, listSchema, body);
}

export function moveListToBoard(
  listId: string,
  body: ListMoveToBoardBody,
): Promise<ListMoveToBoardResult> {
  return post(
    `/lists/${encodeURIComponent(listId)}/move-to-board`,
    listMoveToBoardResultSchema,
    listMoveToBoardBodySchema.parse(body),
  );
}

export function previewListMoveToBoard(
  listId: string,
  query: ListMovePreviewQuery,
): Promise<Reconciliation> {
  const params = listMovePreviewQuerySchema.parse(query);
  const search = new URLSearchParams({ boardId: params.boardId });
  return get(
    `/lists/${encodeURIComponent(listId)}/move-preview?${search.toString()}`,
    reconciliationSchema,
  );
}

export function archiveList(listId: string): Promise<List> {
  return post(`/lists/${encodeURIComponent(listId)}/archive`, listSchema);
}

export function getBoardLists(
  boardId: string,
  state: 'open' | 'archived' = 'open',
): Promise<List[]> {
  return get(
    `/boards/${encodeURIComponent(boardId)}/lists?state=${encodeURIComponent(state)}`,
    listListSchema,
  );
}

export function boardListsQueryOptions(
  boardId: string,
  state: 'open' | 'archived' = 'open',
) {
  return queryOptions({
    queryKey: queryKeys.boards.lists(boardId, state),
    queryFn: () => getBoardLists(boardId, state),
  });
}

export function restoreList(listId: string): Promise<List> {
  return post(`/lists/${encodeURIComponent(listId)}/restore`, listSchema);
}

export function deleteList(listId: string): Promise<null> {
  return del(`/lists/${encodeURIComponent(listId)}`, emptyResponseSchema);
}

// ---------------------------------------------------------------------------
// Variables
// ---------------------------------------------------------------------------

export interface CreateListVariables {
  readonly title: string;
  /** The list immediately LEFT of the gap; null with `nextListId` null means the far right end. */
  readonly previousListId: string | null;
  /** The list immediately RIGHT of the gap; null with `previousListId` null means the far right end. */
  readonly nextListId: string | null;
}

export interface RenameListVariables {
  readonly listId: string;
  readonly title: string;
}

export interface RecolourListVariables {
  readonly listId: string;
  readonly colour: ListColour;
}

export interface MoveListVariables {
  readonly listId: string;
  readonly previousListId: string | null;
  readonly nextListId: string | null;
}

export interface MoveListToBoardVariables {
  readonly listId: string;
  readonly title: string;
  readonly sourceBoardId: string;
  readonly destinationBoardId: string;
}

export interface ArchiveListVariables {
  readonly listId: string;
}

export interface RestoreListVariables {
  readonly listId: string;
  readonly title: string;
}

export interface DeleteListVariables {
  readonly listId: string;
  readonly title: string;
  readonly cardCount: number;
}

// ---------------------------------------------------------------------------
// Optimistic updaters — pure, so they are testable without React
// ---------------------------------------------------------------------------

/**
 * ⚠ Every updater must be PURE AND IMMUTABLE: copy the array, copy the list you
 * change. Mutating in place defeats `lib/optimistic.ts`'s snapshot, and the
 * rollback then restores the object you already mutated — which looks like the
 * rollback silently not working.
 */

/**
 * Builds the placeholder `BoardFaceList` a create inserts. `position` is the
 * empty string — brief §5.2: the server is the sole rank authority, so an
 * optimistic write cannot produce a valid one, and nothing in `apps/web`
 * sorts by `position` (array order is render order), so the empty string is
 * never read as a rank. `openCardCount`/`cardCount` are `0` and `cards` is
 * `[]`: a list nobody has told the server about yet cannot hold a card.
 */
function placeholderList(
  title: string,
  placeholderId: string,
  boardId: string,
): BoardFaceList {
  return {
    id: placeholderId,
    boardId,
    title,
    colour: 'none',
    position: '',
    archived: false,
    isPrList: false,
    cardCount: 0,
    createdAt: '',
    updatedAt: '',
    openCardCount: 0,
    cards: [],
  };
}

/**
 * Inserts the placeholder at the position the neighbours describe — the same
 * three cases the wire has (brief §3.2): both null is the far right end,
 * `nextListId` alone is the far left, `previousListId` alone is immediately
 * after it. This mirrors `POST /lists`'s own reading of the pair, which is
 * deliberately NOT the reading `PATCH /lists/:id/position` gives the same
 * pair (§3.2's asymmetry) — this function must never be reused for a move.
 *
 * Trims `variables.title` and no-ops (returns `previous` unchanged) when the
 * trimmed result is empty — the same client-side refusal `ListComposer`
 * already enforces by disabling its submit control, restated here so this
 * function is safe to call directly, without a UI in front of it. The 422 is
 * the backstop, never the mechanism (brief §3.3).
 */
export function applyOptimisticCreate(
  previous: BoardContents | undefined,
  variables: CreateListVariables,
  placeholderId: string,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  const title = variables.title.trim();
  if (title.length === 0) return previous;

  const placeholder = placeholderList(title, placeholderId, previous.board.id);
  const lists = previous.lists;

  // Both null, or only previousListId given: append at (or after) the named
  // list — the far right end when neither is given.
  if (variables.nextListId === null) {
    if (variables.previousListId === null) {
      return { ...previous, lists: [...lists, placeholder] };
    }
    const index = lists.findIndex((list) => list.id === variables.previousListId);
    if (index === -1) return { ...previous, lists: [...lists, placeholder] };
    return {
      ...previous,
      lists: [...lists.slice(0, index + 1), placeholder, ...lists.slice(index + 1)],
    };
  }

  // nextListId alone (far left), or both given (the gap between them): insert
  // immediately before nextListId either way — previousListId, when also
  // given, is only there to confirm which gap, and the array's own order is
  // what actually determines the insertion point.
  const index = lists.findIndex((list) => list.id === variables.nextListId);
  if (index === -1) return { ...previous, lists: [...lists, placeholder] };
  return {
    ...previous,
    lists: [...lists.slice(0, index), placeholder, ...lists.slice(index)],
  };
}

/**
 * `FR-BOARD-010` AC1: "cards below are unaffected" — changes `title` alone.
 *
 * Trims and no-ops on an empty result, on `applyOptimisticCreate`'s footing —
 * `FR-BOARD-010` AC3: "the rename is rejected and the previous name remains".
 */
export function applyOptimisticRename(
  previous: BoardContents | undefined,
  variables: RenameListVariables,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  const title = variables.title.trim();
  if (title.length === 0) return previous;

  return {
    ...previous,
    lists: previous.lists.map((list) =>
      list.id === variables.listId ? { ...list, title } : list,
    ),
  };
}

/** `FR-BOARD-019`: changes only the list decoration, never title/cards/order. */
export function applyOptimisticRecolour(
  previous: BoardContents | undefined,
  variables: RecolourListVariables,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  return {
    ...previous,
    lists: previous.lists.map((list) =>
      list.id === variables.listId ? { ...list, colour: variables.colour } : list,
    ),
  };
}

/**
 * Reorders the array. Leaves every `position` field untouched — brief §5.2.
 *
 * ⚠ DO NOT "FIX" THIS TO WRITE A NEW `position`. The server is the sole rank
 * authority (brief §3.1): a client-computed rank is not merely redundant, it
 * would be a value nothing minted with `rankBetween` and could collide with a
 * concurrent drag. The array is stale-but-correct until the refetch lands —
 * `position` is stale-and-unused, because nothing in `apps/web` sorts by it.
 */
export function applyOptimisticMove(
  previous: BoardContents | undefined,
  variables: MoveListVariables,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  const lists = previous.lists;
  const fromIndex = lists.findIndex((list) => list.id === variables.listId);
  if (fromIndex === -1) return previous;

  const moving = lists[fromIndex]!;
  const withoutMoving = [...lists.slice(0, fromIndex), ...lists.slice(fromIndex + 1)];

  // Both null cannot happen on the wire (the schema refuses it), but this
  // function is pure array arithmetic and treats it the same as "append at
  // the far right" rather than throwing — the caller (useMoveList) never
  // constructs variables shaped that way.
  let insertAt = withoutMoving.length;
  if (variables.nextListId !== null) {
    const index = withoutMoving.findIndex((list) => list.id === variables.nextListId);
    if (index !== -1) insertAt = index;
  } else if (variables.previousListId !== null) {
    const index = withoutMoving.findIndex((list) => list.id === variables.previousListId);
    if (index !== -1) insertAt = index + 1;
  }

  return {
    ...previous,
    lists: [
      ...withoutMoving.slice(0, insertAt),
      moving,
      ...withoutMoving.slice(insertAt),
    ],
  };
}

/** Removes the list from `contents.lists` entirely — `FR-BOARD-015` AC1. */
export function applyOptimisticArchive(
  previous: BoardContents | undefined,
  variables: ArchiveListVariables,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  return {
    ...previous,
    lists: previous.lists.filter((list) => list.id !== variables.listId),
  };
}

export function applyOptimisticListMoveToBoard(
  previous: BoardContents | undefined,
  variables: MoveListToBoardVariables,
): BoardContents | undefined {
  return removeListFromBoardContents(previous, variables.listId);
}

// ---------------------------------------------------------------------------
// Placeholder identity — brief §5.2
// ---------------------------------------------------------------------------

const OPTIMISTIC_LIST_ID_PREFIX = 'optimistic:';

/**
 * The id an optimistically created list carries until the server answers.
 *
 * Deliberately NOT a ULID. A placeholder that looks real is a placeholder that
 * reaches a route as a neighbour id and 404s for reasons nobody can trace; an
 * `optimistic:` prefix is greppable, and `isOptimisticListId` is the gate.
 */
export function optimisticListId(): string {
  return `${OPTIMISTIC_LIST_ID_PREFIX}${crypto.randomUUID()}`;
}

export function isOptimisticListId(id: string): boolean {
  return id.startsWith(OPTIMISTIC_LIST_ID_PREFIX);
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Trims and refuses an empty result — the client-side backstop for
 * `FR-BOARD-009` and `FR-BOARD-010`'s "whitespace-only is refused" (the 422
 * is the mechanism's last line, not its first).
 */
function trimmedOrThrow(title: string): string {
  const trimmed = title.trim();
  if (trimmed.length === 0) {
    throw new Error('A list title cannot be empty.');
  }
  return trimmed;
}

/**
 * Roll back, then say so — the one home for failure announcements in this file.
 *
 * ⚠ Added by the parent at integration, and the reason is a defect the fan-out
 * produced rather than a tidy-up. Three agents independently chose three homes
 * for this: a hook-level `onError` here, a call-site `onError` in
 * `ListHeader`, and — in `ListComposer` — an unconditional success
 * announcement with no failure path at all. So a **rejected create announced
 * "List Foo added.", rolled back silently, and told the member the opposite of
 * what happened.** Nothing was red; the composer's own tests asserted the
 * announcement fired, and it did.
 *
 * A call site can forget this. A hook cannot: every caller of a `use*List`
 * hook gets the rollback announcement whether or not it thought about failure.
 * That is the whole argument for putting it here, and it is why `ListHeader`'s
 * call-site handler came out.
 *
 * `optimisticMutation`'s own `onError` still does the restoring — this only
 * wraps it, and must always call it first. A silent snap-back is
 * indistinguishable from a bug, which is `announce.ts`'s point: one channel,
 * and no `Notice`, because none of these is an error the member caused.
 */
function announceRollback<TVariables, TContext>(
  handlers: {
    onError: (error: unknown, variables: TVariables, context: TContext) => void;
  },
  message: (variables: TVariables) => string,
): (error: unknown, variables: TVariables, context: TContext) => void {
  return (error, variables, context) => {
    handlers.onError(error, variables, context);
    announce(message(variables));
  };
}

export function useCreateList(
  boardId: string,
): UseMutationResult<List, unknown, CreateListVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  const key = queryKeys.boards.contents(boardId);

  const handlers = optimisticMutation<CreateListVariables, BoardContents>({
    queryClient,
    keys: () => [key],
    updater: (previous, variables) =>
      applyOptimisticCreate(previous, variables, optimisticListId()),
  });

  return useMutation({
    mutationFn: (variables: CreateListVariables) =>
      createList({
        boardId,
        title: trimmedOrThrow(variables.title),
        previousListId: variables.previousListId,
        nextListId: variables.nextListId,
      }),
    ...handlers,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.capacity(boardId),
      });
    },
    onError: announceRollback(handlers, (variables) =>
      t('primitives.listHooks.createFailed', { title: variables.title }),
    ),
  });
}

export function useRenameList(
  boardId: string,
): UseMutationResult<List, unknown, RenameListVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  const key = queryKeys.boards.contents(boardId);

  const handlers = optimisticMutation<RenameListVariables, BoardContents>({
    queryClient,
    keys: () => [key],
    updater: applyOptimisticRename,
  });

  return useMutation({
    mutationFn: (variables: RenameListVariables) =>
      renameList(variables.listId, { title: trimmedOrThrow(variables.title) }),
    ...handlers,
    onError: announceRollback(handlers, () => t('primitives.listHooks.renameFailed')),
  });
}

export function useRecolourList(
  boardId: string,
): UseMutationResult<List, unknown, RecolourListVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  const key = queryKeys.boards.contents(boardId);

  const handlers = optimisticMutation<RecolourListVariables, BoardContents>({
    queryClient,
    keys: () => [key],
    updater: applyOptimisticRecolour,
  });

  return useMutation({
    mutationFn: (variables: RecolourListVariables) =>
      recolourList(variables.listId, { colour: variables.colour }),
    ...handlers,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.lists(boardId, 'open'),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.lists(boardId, 'archived'),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.archiveAll(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.table(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.calendar(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.timeline(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.dashboard(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.map(boardId),
      });
    },
    onError: announceRollback(handlers, () => t('primitives.listHooks.recolourFailed')),
  });
}

export function useMoveList(
  boardId: string,
): UseMutationResult<List, unknown, MoveListVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  const key = queryKeys.boards.contents(boardId);
  const handlers = optimisticMutation<MoveListVariables, BoardContents>({
    queryClient,
    keys: () => [key],
    updater: applyOptimisticMove,
  });

  return useMutation({
    mutationFn: (variables: MoveListVariables) =>
      moveList(variables.listId, {
        previousListId: variables.previousListId,
        nextListId: variables.nextListId,
      }),
    ...handlers,
    // ⚠ Brief §5.3/§3.3: a 409 from the position route is a normal outcome —
    // neighbours out of order, or the gap too small for `rankBetween` — not a
    // crash. No red notice; `announce()` is the only channel, per
    // `announce.ts`'s one-event-one-channel rule.
    onError: announceRollback(handlers, () => t('primitives.listHooks.moveFailed')),
  });
}

export function useMoveListToBoard(
  sourceBoardId: string,
): UseMutationResult<ListMoveToBoardResult, unknown, MoveListToBoardVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables: MoveListToBoardVariables) =>
      moveListToBoard(variables.listId, { boardId: variables.destinationBoardId }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.boards.contents(sourceBoardId),
      });
      const previous = queryClient.getQueriesData<BoardContents>({
        queryKey: queryKeys.boards.contents(sourceBoardId),
      });
      setBoardContentsQueries(queryClient, sourceBoardId, (current) =>
        removeListFromBoardContents(current, variables.listId),
      );
      return { previous };
    },
    onError: (_error, variables, context) => {
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
      announce(t('primitives.listHooks.moveToBoardFailed', { title: variables.title }));
    },
    onSuccess: (_result, variables) => {
      for (const key of boardMoveInvalidationKeys(
        sourceBoardId,
        variables.destinationBoardId,
      )) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.capacity(sourceBoardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.capacity(variables.destinationBoardId),
      });
    },
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(sourceBoardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(variables.destinationBoardId),
      });
    },
  });
}

export function useArchiveList(
  boardId: string,
): UseMutationResult<List, unknown, ArchiveListVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  const key = queryKeys.boards.contents(boardId);

  const handlers = optimisticMutation<ArchiveListVariables, BoardContents>({
    queryClient,
    keys: () => [key],
    updater: applyOptimisticArchive,
  });

  return useMutation({
    mutationFn: (variables: ArchiveListVariables) => archiveList(variables.listId),
    ...handlers,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.capacity(boardId),
      });
    },
    onError: announceRollback(handlers, () => t('primitives.listHooks.archiveFailed')),
  });
}

export function useRestoreList(
  boardId: string,
): UseMutationResult<List, unknown, RestoreListVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables: RestoreListVariables) => restoreList(variables.listId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.archiveAll(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.capacity(boardId),
      });
    },
    onError: () => {
      announce(t('primitives.listHooks.restoreFailed'));
    },
  });
}

export function useDeleteList(
  boardId: string,
): UseMutationResult<null, unknown, DeleteListVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables: DeleteListVariables) => deleteList(variables.listId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.archiveAll(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.capacity(boardId),
      });
    },
    onError: () => {
      announce(t('primitives.listHooks.deleteFailed'));
    },
  });
}

export interface SetPrListVariables {
  readonly listId: string;
  readonly isPrList: boolean;
}

/**
 * Designating a list clears the flag on the board's previous PR list in the
 * same server transaction, so no optimistic write is attempted: the board
 * contents are invalidated and refetched instead, which also brings the card
 * fronts' `approvals` badges.
 */
export function useSetListPrList(
  boardId: string,
): UseMutationResult<List, unknown, SetPrListVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();

  return useMutation({
    mutationFn: (variables: SetPrListVariables) =>
      setListPrList(variables.listId, { isPrList: variables.isPrList }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.lists(boardId, 'open'),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.lists(boardId, 'archived'),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.cards.all() });
    },
    onError: () => {
      announce(t('boardFace.list.prListFailed'));
    },
  });
}

/**
 * ⏭ FORWARD — `restoreList`, `deleteList` and the archived-items panel are
 * chunk `F3`'s (`FE-22.2`, `FE-22.3`), not this chunk's. `C2-BRIEF.md` §2.1
 * carries the argument: `FR-EXP-010` and `FR-EXP-011` already own those
 * surfaces by name, they live in the board menu `FE-08.2` owns, and they are
 * MVP where `FR-BOARD-015`/`016` are V1. The routes exist —
 * `GET /boards/:id/lists?state=archived`, `POST /lists/:id/restore`,
 * `DELETE /lists/:id` (409 unless already archived) — so F3 adds its endpoint
 * functions HERE rather than opening a second lists module.
 */
